import sapiConfig from "../configs/sapi";
import mapiConfig from "../configs/mapi";
import { httpMStepsClient } from "./utils/httpService";
import { logError } from "./utils/logService";

import * as Sentry from "@sentry/browser";

//BASE_URL
const BASE_URL = sapiConfig.api.domain;
const MAPI_URL = mapiConfig.api.domain;

const getLocation = (location: string) => {
  return location.replace("Australia/", "");
};

export const getDelivery = async (
  searchText: string,
  team: string,
  location: string,
  endpoint: string,
  tableHeaders: Record<string, Record<string, string | number | boolean>>,
) => {
  const methodPath = `/report/${endpoint}?${getLocation(
    location,
  )}&${team.toLowerCase()}&${searchText}`;

  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  });
};

export const getDeliveryAttachment = async (DocNum: string) => {
  const methodPath = `/delivery/${DocNum}`;

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: methodPath,
  });
};

export const getAllDeliveryAttachments = async (
  page: number = 1,
  pageSize: number = 10,
  sort: string = "-createdAt",
  search: string = "",
) => {
  const methodPath = `/delivery/list?page=${page}&pageSize=${pageSize}&sort=${sort}&search=${search}`;

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: methodPath,
  });
};

export const addDeliveryAttachment = async (
  DocNum: string,
  fileName: string,
  remarks: string,
  soOperator?: string,
) => {
  const methodPath = `/delivery/add`;

  await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "POST",
    path: methodPath,
    body: {
      DocNum: DocNum,
      attachment: {
        fileName: fileName,
      },
      remarks: remarks,
      soOperator: soOperator,
    },
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const updateDeliveryAttachment = async (
  DocNum: string,
  fileName: string,
  remarks: string,
  soOperator?: string,
) => {
  const methodPath = `/delivery/update`;

  await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PUT",
    path: methodPath,
    body: {
      DocNum: DocNum,
      attachment: {
        fileName: fileName,
      },
      remarks: remarks,
      soOperator: soOperator,
    },
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });
};

export const updateDeliveryPortalStatusAddress = async (payload: any) => {
  const methodPath = `/go/order`;

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: methodPath,
    body: payload,
  });
};

export const getPortalOrderByOrderNo = async (orderNo: string) => {
  const methodPath = `/go/order/${orderNo}`;

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: methodPath,
  });
};

export const getOrderByDocNum = async (docNum: string) => {
  const methodPath = `/report/order_by_docnum/${docNum}`;

  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  });
};

export interface DeliveryReportV2Params {
  page: number;
  pageSize: number;
  sort?: any;
  search?: string;
  states?: string[];
  statuses?: string[];
  profile?: string;
  city?: string;
  salesName?: string;
  onlyToday?: boolean;
  fetchAll?: boolean;
  startDate?: string | null;
  endDate?: string | null;
}

export const getDeliveryReportV2 = async ({
  page,
  pageSize,
  sort,
  search,
  states,
  statuses,
  profile,
  city,
  salesName,
  onlyToday,
  fetchAll,
  startDate,
  endDate,
}: DeliveryReportV2Params) => {
  const sortStr =
    typeof sort === "object" && sort?.key
      ? `${sort.direction === "desc" ? "-" : ""}${sort.key}`
      : sort;

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));

  // if (sortStr) params.set("sort", sortStr);
  if (search) params.set("search", search);

  if (states && states.length > 0) params.set("state", states.join(","));
  if (statuses && statuses.length > 0) params.set("status", statuses.join(","));

  if (salesName) params.set("salesName", salesName);
  if (profile) params.set("profile", profile);
  if (city) params.set("city", getLocation(city));
  if (onlyToday) params.set("onlyToday", String(onlyToday));
  if (fetchAll) params.set("fetchAll", String(fetchAll));
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);

  const methodPath = `/delivery/report?${params.toString()}`;

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: methodPath,
  });
};
